/**
 * MarkdownView — single source of truth for rendering AI-generated markdown
 * in CountBucks. Used by:
 *   • `AIHub.jsx` chat message bubbles (assistant + user)
 *   • `DocumentPreviewModal.jsx` saved-transcript preview
 *
 * Why a wrapper instead of inline `<ReactMarkdown>`? Two reasons:
 *   1. We map every block-level element to Tailwind-styled tags so the
 *      output matches the rest of the app without depending on the
 *      `@tailwindcss/typography` plugin (which isn't installed).
 *   2. Inline code, links, lists, and code blocks get consistent styling
 *      between chat and saved-document preview — no surprises when a
 *      conversation gets exported.
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../lib/utils';

export default function MarkdownView({ children, className }) {
  if (!children) return null;
  return (
    <div className={cn('markdown-view text-sm leading-relaxed', className)} data-testid="markdown-view">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...p }) => <h1 className="text-xl font-bold mt-4 mb-2 first:mt-0" {...p} />,
          h2: ({ node, ...p }) => <h2 className="text-lg font-bold mt-4 mb-2 first:mt-0" {...p} />,
          h3: ({ node, ...p }) => <h3 className="text-base font-semibold mt-3 mb-1.5 first:mt-0" {...p} />,
          h4: ({ node, ...p }) => <h4 className="text-sm font-semibold mt-3 mb-1 first:mt-0" {...p} />,
          h5: ({ node, ...p }) => <h5 className="text-xs font-semibold uppercase tracking-wide mt-2 mb-1 first:mt-0" {...p} />,
          h6: ({ node, ...p }) => <h6 className="text-xs font-semibold uppercase tracking-wide opacity-80 mt-2 mb-1 first:mt-0" {...p} />,
          p: ({ node, ...p }) => <p className="my-2 first:mt-0 last:mb-0" {...p} />,
          ul: ({ node, ordered, ...p }) => <ul className="list-disc pl-5 my-2 space-y-1" {...p} />,
          ol: ({ node, ordered, ...p }) => <ol className="list-decimal pl-5 my-2 space-y-1" {...p} />,
          li: ({ node, ...p }) => <li className="leading-snug" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-semibold" {...p} />,
          em: ({ node, ...p }) => <em className="italic" {...p} />,
          a: ({ node, ...p }) => (
            <a className="text-emerald-600 dark:text-emerald-400 underline underline-offset-2 hover:text-emerald-500" target="_blank" rel="noopener noreferrer" {...p} />
          ),
          blockquote: ({ node, ...p }) => (
            <blockquote className="border-l-4 border-emerald-500/60 pl-3 my-3 italic text-zinc-600 dark:text-zinc-400" {...p} />
          ),
          hr: () => <hr className="my-4 border-zinc-200 dark:border-white/10" />,
          // Inline images — used by saved Nano Banana documents which embed
          // the image via `![title](url)`. Constrain width so the preview
          // modal doesn't overflow horizontally on small screens.
          img: ({ node, alt, ...p }) => (
            <img
              alt={alt || ''}
              className="my-3 max-w-full h-auto rounded-md border border-zinc-200 dark:border-white/10"
              loading="lazy"
              {...p}
            />
          ),
          // Tables (gfm)
          table: ({ node, ...p }) => (
            <div className="my-3 overflow-x-auto">
              <table className="min-w-full text-xs border border-zinc-200 dark:border-white/10 rounded" {...p} />
            </div>
          ),
          thead: ({ node, ...p }) => <thead className="bg-zinc-100 dark:bg-zinc-800" {...p} />,
          th: ({ node, ...p }) => <th className="px-2.5 py-1.5 text-left font-medium border-b border-zinc-200 dark:border-white/10" {...p} />,
          td: ({ node, ...p }) => <td className="px-2.5 py-1.5 border-b border-zinc-200 dark:border-white/5" {...p} />,
          // Code: distinguish inline vs. fenced
          code: ({ node, inline, className: cls, children: kids, ...rest }) => {
            if (inline) {
              return (
                <code
                  className="bg-zinc-100 dark:bg-zinc-800 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded text-[0.85em] font-mono"
                  {...rest}
                >
                  {kids}
                </code>
              );
            }
            return (
              <code className={cn('font-mono text-xs', cls)} {...rest}>
                {kids}
              </code>
            );
          },
          pre: ({ node, ...p }) => (
            <pre className="bg-zinc-900 text-zinc-100 dark:bg-black/60 rounded-md p-3 my-2 overflow-x-auto text-xs" {...p} />
          ),
        }}
      >
        {String(children)}
      </ReactMarkdown>
    </div>
  );
}
